// Modulo della prenotazione (nuova o modifica) e scheda della prenotazione: stesso comportamento del prototipo
// approvato (prototipo/agenda.html). I controlli sono quelli di logica/agenda.js; qui c'è solo la pagina.
// Salute (GDPR art. 9): la sezione c'è solo per responsabile, operatrice e sviluppo. Il ricevimento non la vede
// e non la chiede nemmeno al database. I dati di salute vanno solo in «salute/<idOspite>», mai nella prenotazione.
import { t } from '../i18n.js';
import {
  h, sostituisci, apriFoglio, chiudiFoglio, seguiInvio,
} from './dom.js';
import { giornoLungo, quandoBreve } from './date.js';
import {
  SALE, LETTINI, OPERATRICI, TIPI, PAGA_ESTERNO, toMin, hm, fine, occupa, esterno, salaNome, nomeOperatrice, inTurno,
  numero, orari, eur, ridotto, incasso, controllaPrenotazione, primoLibero, problemi, adessoRoma, esitoDisdetta, azioniStato,
  noShowDaDecidere, importoNoShow,
} from '../logica/agenda.js';
import { TRATTAMENTI, categorie, visibili } from '../logica/listino.js';
import { puoPrenotare, vedeSalute, miaColonna, admin } from '../logica/ruoli.js';
import {
  ascoltaGiornoIntero, ascoltaPrenotazione, ascoltaStorico, ascoltaSalute, idSalute, saluteVuota,
} from '../dati/prenotazioni.js';

export const testiInvio = () => ({ telefono: t('invii.telefono'), errore: (codice) => t('invii.errore', { codice }) });

// ---------- pezzi del modulo ----------
const opz = (value, testo, scelto = false) => h('option', { value, selected: scelto }, testo);
export const campo = (etichetta, controllo, attr = {}) => h('label', attr, h('span', {}, etichetta), controllo);
export const spunta = (testo, input, attr = {}) => h('label', { classe: 'check', ...attr }, input, h('span', {}, testo));
const scheda = (titolo, ...figli) => h('fieldset', {}, h('legend', {}, titolo), ...figli);

/**
 * Scelta a pulsanti, come nella tela (sala, lettino): uno solo premuto alla volta.
 * Si legge e si cambia con .value come una select; il tocco manda «change» al modulo.
 * @param {Array<[string, string, string?]>} voci [valore, testo, classe del pallino]
 */
function scelte(id, etichetta, voci, scelto) {
  const box = h('div', { classe: 'scelte', role: 'group', id, 'aria-label': etichetta });
  const metti = (v) => {
    box.dataset.valore = v;
    for (const b of box.children) b.setAttribute('aria-pressed', String(b.dataset.v === v));
  };
  box.append(...voci.map(([v, testo, pallino]) => h('button', {
    type: 'button', dati: { v },
    onclick: () => { metti(v); box.dispatchEvent(new Event('change', { bubbles: true })); },
  }, pallino ? h('span', { classe: `pallino ${pallino}`, 'aria-hidden': 'true' }) : null, testo)));
  Object.defineProperty(box, 'value', { get: () => box.dataset.valore, set: (v) => metti(String(v)) });
  metti(String(scelto));
  return box;
}

function opzioniTrattamenti(scelto) {
  const voci = visibili(scelto);
  return categorie()
    .map((c) => h('optgroup', { label: c }, voci.filter((x) => x.cat === c).map((x) => opz(x.id,
      `${x.nome} ${x.durata}' · ${x.prezzo == null ? t('modulo.prezzoIgnoto') : eur(x.prezzo)}`, x.id === scelto))))
    .filter((g) => g.children.length);
}

function opzioniOra(scelta) {
  const lista = orari();
  if (scelta && !lista.includes(scelta)) lista.push(scelta);
  return lista.sort((a, b) => toMin(a) - toMin(b)).map((x) => opz(x, hm(toMin(x)), x === scelta));
}

/** Chi paga davvero (a pagamento e con importo): solo per lui ha senso un addebito. */
const pagante = (b) => b.tipo === 'pagamento' && b.importo > 0;

// Testo dello stato, come nel prototipo (la disdetta dice se c'è l'addebito).
export function testoStato(b) {
  if (b.stato === 'disdetta') return b.addebito ? t('stato.disdettaTardiva') : t('stato.disdettaGratuita');
  if (b.stato === 'no-show') {
    if (!pagante(b)) return t('stato.no-show');
    if (noShowDaDecidere(b)) return t('stato.noShowDaDecidere');
    return incasso(b) ? t('stato.noShowAddebito') : t('stato.noShowGratis');
  }
  return t(`stato.${b.stato}`);
}

// Gravidanza, allergie e note di una persona (solo personale SPA).
function campiSalute(id, valori = null) {
  const grav = h('input', { type: 'checkbox', id: `${id}-grav`, checked: !!valori?.grav });
  const all = h('input', { type: 'checkbox', id: `${id}-all`, checked: !!valori?.all });
  const note = h('input', { id: `${id}-note`, value: valori?.note || '', autocomplete: 'off', maxlength: 300 });
  const titolo = h('legend', {}, t('salute.titolo'));
  const box = h('fieldset', { classe: 'salute', id },
    titolo, h('div', { classe: 'riga-spunte' }, spunta(t('salute.grav'), grav), spunta(t('salute.all'), all)), campo(t('salute.note'), note),
    h('p', { classe: 'hint' }, t('salute.spiega')));
  return {
    box,
    titolo,
    leggi: () => ({ grav: grav.checked, all: all.checked, note: note.value.trim() }),
    metti(x) { grav.checked = !!x?.grav; all.checked = !!x?.all; note.value = x?.note || ''; },
    blocca(si) { for (const el of [grav, all, note]) el.disabled = si; },
    bloccato: () => grav.disabled,
  };
}

/**
 * Modulo della prenotazione.
 * @param {object} vista dall'agenda: { ctx, ruoli, scritture, data(), dati(), nomi(), vaiA(k) }
 * @param {{ prenotazione?: object, richiesta?: object, data?: string, inizio?: string, sala?: string, lettino?: number, op?: string }} pre
 *   prenotazione = modifica · gli altri campi = spazio libero toccato nell'agenda (ora e colonna già pronte) ·
 *   richiesta = richiesta di RVC dal ponte: ospite e trattamento già scritti, giorno e ora fissi (quelli chiesti);
 *   salvando, la risposta «confermata» va nello stesso lotto
 */
export function apriModulo(vista, pre = {}) {
  const ed = pre.prenotazione || null;
  const ric = pre.richiesta || null;
  const conSalute = vedeSalute(vista.ruoli);
  const nomi = vista.nomi();
  const data0 = ric ? pre.data : ed ? ed.data : (pre.data || vista.data());
  const agenda = vista.dati();
  const giorno0 = agenda?.data === data0 ? agenda : null;
  const primaInTurno = OPERATRICI.find((o) => inTurno(giorno0, o)) || OPERATRICI[0];
  const tr0 = ric && TRATTAMENTI[ric.tr] ? ric.tr : 'essentzia';
  // Spostamento chiesto da RVC su una prenotazione già legata: nuovo giorno e ora, il resto com'era.
  const v = ed ? { ...ed, ...(ric && { data: pre.data, inizio: pre.inizio, sala: pre.sala, lettino: pre.lettino, op: pre.op }) } : {
    data: data0, inizio: pre.inizio || '11:00', tr: tr0, sala: pre.sala || 'flow', lettino: pre.lettino || 1,
    op: pre.op || primaInTurno, camera: ric ? (ric.esterno ? 'EST.' : ric.camera || '') : '', nome: ric?.nome || '',
    tipo: ric?.pagamento || 'pagamento', importo: TRATTAMENTI[tr0].prezzo, motivo: '', motivoSconto: '', magg: false,
    ...(ric?.esterno && { est: { tel: ric.esterno.tel || '', email: ric.esterno.email || '', paga: 'pos', caparra: 0 } }),
  };
  const altra = OPERATRICI.find((o) => o !== v.op && inTurno(giorno0, o)) || OPERATRICI.find((o) => o !== v.op);
  const est = v.est || { tel: '', email: '', paga: 'pos', caparra: 0 };
  const prezzo0 = TRATTAMENTI[v.tr]?.prezzo;

  const q = {
    cam: h('input', { id: 'f-cam', value: v.camera || '', placeholder: t('modulo.cameraEs'), autocomplete: 'off', maxlength: 12 }),
    nome: h('input', { id: 'f-nome', value: v.nome || '', autocomplete: 'off', maxlength: 60 }),
    est: h('input', { type: 'checkbox', id: 'f-est', checked: esterno(v.camera) }),
    tel: h('input', { id: 'f-tel', type: 'tel', inputmode: 'tel', value: est.tel || '', autocomplete: 'off', placeholder: '+39 …', maxlength: 30 }),
    mail: h('input', { id: 'f-mail', type: 'email', inputmode: 'email', value: est.email || '', autocomplete: 'off', maxlength: 80 }),
    paga: h('select', { id: 'f-paga' }, PAGA_ESTERNO.map((k) => opz(k, t(`paga.${k}`), k === est.paga))),
    cap: h('input', { id: 'f-cap', inputmode: 'decimal', value: est.caparra || '', placeholder: '0' }),
    coppia: ed ? null : h('input', { type: 'checkbox', id: 'f-coppia' }),
    tr: h('select', { id: 'f-tr' }, opzioniTrattamenti(v.tr)),
    nome2: h('input', { id: 'f-nome2', placeholder: t('modulo.nome2Vuoto'), autocomplete: 'off', maxlength: 60 }),
    tr2: h('select', { id: 'f-tr2' }, opzioniTrattamenti(v.tr)),
    op2: h('select', { id: 'f-op2' }),
    impL2: h('span', {}, t('importo.pagamento')),
    imp2: h('input', { id: 'f-imp2', inputmode: 'decimal', value: prezzo0 ?? '' }),
    data: h('input', { type: 'date', id: 'f-data', value: v.data }),
    ora: h('select', { id: 'f-ora' }, opzioniOra(v.inizio)),
    libero: h('button', { type: 'button', classe: 'largo', id: 'f-libero' }, t('modulo.primoLibero')),
    liberoMsg: h('p', { classe: 'hint', id: 'f-lib-msg', 'aria-live': 'polite' }),
    sala: scelte('f-sala', t('modulo.sala'), SALE.map((s) => [s.id, s.nome, `p-${s.id}`]), v.sala),
    let: scelte('f-let', t('modulo.lettino'), LETTINI.map((l) => [String(l), String(l)]), v.lettino),
    op: h('select', { id: 'f-op' }),
    dur: h('input', { id: 'f-dur', inputmode: 'numeric', value: v.durata ?? TRATTAMENTI[v.tr]?.durata ?? '' }),
    tipo: h('select', { id: 'f-tipo' }, TIPI.map((k) => opz(k, t(`tipo.${k}`), k === v.tipo))),
    impL: h('span', { id: 'f-imp-l' }, t('importo.pagamento')),
    imp: h('input', { id: 'f-imp', inputmode: 'decimal', value: v.importo ?? '' }),
    lis: h('span', { classe: 'et et-salvia', id: 'f-lis' }),
    sc: h('input', { id: 'f-sc', value: v.motivoSconto || '', placeholder: t('modulo.scontoEs'), autocomplete: 'off', maxlength: 120 }),
    mot: h('input', { id: 'f-mot', value: v.motivo || '', placeholder: t('modulo.complEs'), autocomplete: 'off', maxlength: 120 }),
    magg: h('input', { type: 'checkbox', id: 'f-magg', checked: !!v.magg }),
    msg: h('ul', { classe: 'msgs controlli', id: 'f-msg', 'aria-live': 'polite', 'aria-label': t('modulo.controlli') }),
    salva: h('button', { type: 'submit', classe: 'primario', id: 'f-salva' }, ed ? t('modulo.salvaModifiche') : t('modulo.salvaNuova')),
  };
  // Richiesta di RVC: giorno, ora e persone sono quelli chiesti (la conferma vale per quelli).
  if (ric) {
    q.data.disabled = true;
    q.ora.disabled = true;
    q.libero.hidden = true;
    if (q.coppia) { q.coppia.checked = ric.persone === 2; q.coppia.disabled = true; }
  }
  const s1 = conSalute ? campiSalute('f-salute') : null;
  const s2 = conSalute && !ed ? campiSalute('f-salute2') : null;
  if (s2) s2.titolo.textContent = t('salute.titoloN', { n: 2 });
  const coppia = () => !!q.coppia?.checked;

  const estBox = h('div', { id: 'f-est-box', hidden: true },
    h('div', { classe: 'riga2' }, campo(t('modulo.telefono'), q.tel), campo(t('modulo.email'), q.mail)),
    h('div', { classe: 'riga2' }, campo(t('modulo.paga'), q.paga), campo(t('modulo.caparra'), q.cap)),
    h('p', { classe: 'hint' }, t('modulo.esternoSpiega')));
  const coppiaBox = h('div', { classe: 'riquadro', id: 'f-coppia-box', hidden: true },
    h('h3', {}, t('modulo.seconda')),
    campo(t('modulo.nome'), q.nome2), campo(t('modulo.trattamento'), q.tr2),
    h('div', { classe: 'riga2' }, campo(t('modulo.operatrice'), q.op2), h('label', {}, q.impL2, q.imp2)));
  // Sala e lettino sono pulsanti: niente <label> intorno (il tocco sul testo premerebbe il primo pulsante).
  const salaL = h('div', { classe: 'campo' }, h('span', {}, t('modulo.sala')), q.sala);
  const letL = h('div', { classe: 'campo', id: 'f-let-l' }, h('span', {}, t('modulo.lettino')), q.let);
  const scL = campo(t('modulo.motivoSconto'), q.sc, { id: 'f-sc-l', classe: 'da-scrivere', hidden: true });
  const motL = campo(t('modulo.motivoCompl'), q.mot, { id: 'f-mot-l', classe: 'da-scrivere', hidden: true });

  // Schede come nella tela: Ospite · Trattamento (e coppia) · Quando e dove · Pagamento · Salute · Controlli.
  const form = h('form', { id: 'f', novalidate: true },
    ric ? scheda(t('ponte.daRvc'),
      h('p', { id: 'f-ric' }, t('ponte.chiesta', { chi: ric.daNome || 'RVC', quando: giornoLungo(ric.data), ora: hm(toMin(ric.inizio)) })),
      ric.nota ? h('p', { classe: 'nota' }, ric.nota) : null) : null,
    scheda(t('modulo.ospite'),
      h('div', { classe: 'riga2 camera-nome' }, campo(t('modulo.camera'), q.cam), campo(t('modulo.nome'), q.nome)),
      spunta(t('modulo.esterno'), q.est),
      estBox),
    scheda(t('modulo.trattamento'),
      campo(t('modulo.dalListino'), q.tr),
      h('div', { classe: 'riga-et' }, q.lis, h('span', { classe: 'hint' }, t('modulo.daSoli'))),
      ed ? (ed.coppia ? h('p', { classe: 'hint' }, t('modulo.inCoppia')) : null) : spunta(t('modulo.coppia'), q.coppia),
      coppiaBox),
    scheda(t('modulo.quandoDove'),
      h('div', { classe: 'riga2' }, campo(t('modulo.giorno'), q.data), campo(t('modulo.ora'), q.ora)),
      q.libero, q.liberoMsg,
      salaL,
      h('div', { classe: 'riga2 lettino-op' }, letL, campo(t('modulo.operatrice'), q.op))),
    scheda(t('modulo.pagamento'),
      h('div', { classe: 'riga2' }, campo(t('modulo.tipo'), q.tipo), h('label', {}, q.impL, q.imp)),
      h('div', { classe: 'riga2 in-fondo' }, campo(t('modulo.durata'), q.dur), spunta(t('modulo.magg'), q.magg)),
      scL, motL),
    s1?.box,
    s2?.box,
    q.msg,
    h('div', { classe: 'azioni due-terzi' }, h('button', { type: 'button', onclick: () => chiudiFoglio() }, t('modulo.annulla')), q.salva),
    h('p', { classe: 'hint centrato' }, t('modulo.soloServer')));

  const { testa, alChiudere } = apriFoglio({
    sopra: ed ? t('modulo.modifica') : t('modulo.nuova'), titolo: giornoLungo(v.data), chiudi: t('foglio.chiudi'),
  }, form);

  // Dati del giorno scelto nel modulo (può essere diverso da quello dell'agenda): servono per tutti i controlli.
  let dati = null;
  let datiDi = '';
  let stopDati = () => {};
  function seguiGiorno(k) {
    if (k === datiDi) return;
    stopDati();
    dati = null;
    datiDi = k;
    stopDati = k ? ascoltaGiornoIntero(vista.ctx, k, (x) => { dati = x; riempiOperatrici(); sync(); },
      (e) => vista.ctx.scatola.registra(e, { dove: 'modulo-giorno' })) : () => {};
  }

  // Operatrici con «non in turno» accanto, per il giorno scelto. Si rifà solo se cambia qualcosa.
  let opTesti = '';
  function riempiOperatrici() {
    const testi = OPERATRICI.map((o) => nomeOperatrice(o, nomi) + (dati && !inTurno(dati, o) ? ` · ${t('turni.fuori')}` : ''));
    if (testi.join('|') === opTesti) return;
    opTesti = testi.join('|');
    const a = q.op.value || v.op;
    const b = q.op2.value || altra;
    sostituisci(q.op, OPERATRICI.map((o, i) => opz(o, testi[i], o === a)));
    sostituisci(q.op2, OPERATRICI.map((o, i) => opz(o, testi[i], o === b)));
  }

  // Scheda salute già scritta (solo in modifica e solo per il personale SPA).
  let saluteOrig = null;
  let saluteCaricata = !(s1 && ed);
  let stopSalute = () => {};
  if (s1 && ed) {
    s1.blocca(true);
    stopSalute = ascoltaSalute(vista.ctx, idSalute(ed), (x) => {
      stopSalute();
      saluteOrig = x;
      s1.metti(x);
      s1.blocca(false);
      saluteCaricata = true;
      sync();
    }, (e) => {
      // Senza la scheda (es. permesso negato) la prenotazione si salva lo stesso, la salute no.
      vista.ctx.scatola.registra(e, { dove: 'modulo-salute' });
      saluteCaricata = true;
      sync();
    });
  }
  alChiudere(() => { stopDati(); stopSalute(); });

  function candidati() {
    const tr = TRATTAMENTI[q.tr.value];
    const isEst = q.est.checked;
    const tipo = q.tipo.value;
    const base = {
      data: q.data.value,
      inizio: q.ora.value,
      sala: q.sala.value,
      camera: isEst ? 'EST.' : q.cam.value.trim(),
      tipo,
      est: isEst ? { tel: q.tel.value.trim(), email: q.mail.value.trim(), paga: q.paga.value, caparra: numero(q.cap.value) } : null,
      motivo: tipo === 'complimentary' ? q.mot.value.trim() : '',
      magg: q.magg.checked,
    };
    const c1 = {
      ...base, tr: tr.id, durata: Math.round(numero(q.dur.value)) || tr.durata, listino: tr.prezzo,
      lettino: coppia() ? 1 : +q.let.value, op: q.op.value, nome: q.nome.value.trim(), importo: numero(q.imp.value),
    };
    const cs = [c1];
    if (coppia()) {
      const tr2 = TRATTAMENTI[q.tr2.value];
      cs.push({
        ...base, tr: tr2.id, durata: tr2.id === tr.id ? c1.durata : tr2.durata, listino: tr2.prezzo,
        lettino: 2, op: q.op2.value, nome: q.nome2.value.trim() || c1.nome, importo: numero(q.imp2.value),
      });
    }
    const sc = ridotto(cs) ? q.sc.value.trim() : '';
    for (const c of cs) c.motivoSconto = sc;
    return cs;
  }

  function saluti() {
    if (!s1) return [];
    return coppia() ? [s1.leggi(), s2.leggi()] : [s1.leggi()];
  }

  function controlla(cs = candidati()) {
    const sal = saluti();
    const attesa = !dati || !saluteCaricata;
    const r = dati ? controllaPrenotazione(cs, {
      giorno: dati, ignora: ed ? [ed.id] : [], nomi, inCoppia: !!ed?.coppia, salute: s1 ? { grav: sal.some((x) => x.grav) } : null,
    }) : { errori: [], avvisi: [] };
    sostituisci(q.msg,
      attesa ? h('li', { classe: 'i' }, t('modulo.carico')) : null,
      r.errori.map((x) => h('li', { classe: 'e' }, x)),
      r.avvisi.map((x) => h('li', { classe: 'w' }, x)),
      attesa || r.errori.length ? null : h('li', { classe: 'ok' }, t('modulo.tuttoOk')));
    q.salva.disabled = attesa || r.errori.length > 0;
    return { cs, sal, ok: !attesa && !r.errori.length };
  }

  function sync(e) {
    const id = e?.target?.id;
    if (id === 'f-tr') {
      const x = TRATTAMENTI[q.tr.value];
      q.imp.value = x.prezzo ?? '';
      q.dur.value = x.durata;
      if (x.sala) q.sala.value = x.sala;
      if (!q.tr2.dataset.toccato) { q.tr2.value = x.id; q.imp2.value = x.prezzo ?? ''; }
    }
    if (id === 'f-tr2') { q.tr2.dataset.toccato = '1'; q.imp2.value = TRATTAMENTI[q.tr2.value].prezzo ?? ''; }
    if (id === 'f-data') {
      seguiGiorno(q.data.value);
      testa.querySelector('h2').textContent = giornoLungo(q.data.value);
    }
    const doppia = coppia();
    coppiaBox.hidden = !doppia;
    if (s2) s2.box.hidden = !doppia;
    const isEst = q.est.checked;
    estBox.hidden = !isEst;
    q.cam.disabled = isEst;
    if (isEst) q.cam.value = 'EST.';
    else if (id === 'f-est') q.cam.value = '';
    if (id && id !== 'f-libero') q.liberoMsg.textContent = '';
    letL.hidden = doppia;
    const tipo = q.tipo.value;
    motL.hidden = tipo !== 'complimentary';
    q.impL.textContent = t(`importo.${tipo}`);
    q.impL2.textContent = t(`importo.${tipo}`);
    if (s1) s1.titolo.textContent = doppia ? t('salute.titoloN', { n: 1 }) : t('salute.titolo');
    const cs = candidati();
    const lis = cs.map((c) => (c.listino == null ? t('modulo.prezzoIgnoto') : `${eur(c.listino)} · ${TRATTAMENTI[c.tr].durata}'`));
    q.lis.textContent = t('modulo.listino', { prezzo: cs.length === 2 ? t('modulo.listino2', { a: lis[0], b: lis[1] }) : lis[0] });
    scL.hidden = !ridotto(cs);
    controlla(cs);
  }

  // S5: il primo orario dove tutto è libero (lettino, sala in servizio, operatrice in turno, fuori dalla P.P).
  q.libero.addEventListener('click', () => {
    if (!dati) return;
    const r = primoLibero(candidati(), dati, { ignora: ed ? [ed.id] : [], adesso: adessoRoma() });
    if (r === null) { q.liberoMsg.textContent = t('modulo.liberoGiorno'); return; }
    if (!r.length) { q.liberoMsg.textContent = t('modulo.liberoNo'); return; }
    const [c1, c2] = r;
    sostituisci(q.ora, opzioniOra(c1.inizio));
    q.sala.value = c1.sala;
    q.let.value = String(c1.lettino);
    q.op.value = c1.op;
    if (c2) q.op2.value = c2.op;
    sync({ target: q.libero });
    q.liberoMsg.textContent = t('modulo.libero', {
      ora: hm(toMin(c1.inizio)),
      dove: c2 ? salaNome(c1.sala) : `${salaNome(c1.sala)} ${c1.lettino}`,
      chi: c2 ? `${nomeOperatrice(c1.op, nomi)} + ${nomeOperatrice(c2.op, nomi)}` : nomeOperatrice(c1.op, nomi),
    });
  });

  form.addEventListener('input', sync);
  form.addEventListener('change', sync);
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const { cs, sal, ok } = controlla();
    if (!ok) return;
    q.salva.disabled = true;
    let r;
    const aggiungi = ric ? vista.scritture.rispostaNelLotto(ric, 'confermata') : null;
    if (ed) {
      // La scheda salute si riscrive solo se c'è qualcosa (o se prima c'era): mai per il ricevimento.
      const scrivi = s1 && !s1.bloccato() && (!saluteVuota(sal[0]) || !saluteVuota(saluteOrig));
      r = vista.scritture.modifica(ed.id, cs[0], { nota: cs[0].motivoSconto || '', salute: scrivi ? sal[0] : null, ospite: idSalute(ed), aggiungi });
    } else {
      r = vista.scritture.crea(cs, { salute: sal.map((x) => (saluteVuota(x) ? null : x)), aggiungi });
    }
    chiudiFoglio();
    if (cs[0].data !== vista.data()) vista.vaiA(cs[0].data);
    seguiInvio(r.conferma, ric ? t('ponte.salvataConferma') : ed ? t('salvato.modifica') : t('salvato.nuova'), testiInvio());
  });

  riempiOperatrici();
  seguiGiorno(v.data);
  sync();
}

// ---------- scheda della prenotazione ----------
function testoEvento(e, nomi) {
  const x = e.dopo || {};
  if (e.tipo === 'crea' || e.tipo === 'modifica') {
    const dove = x.inizio ? t('evento.dove', {
      ora: hm(toMin(x.inizio)), sala: salaNome(x.sala), lettino: x.lettino, op: nomeOperatrice(x.op, nomi),
    }) : '';
    return `${t(`evento.${e.tipo}`)}${dove ? `: ${dove}` : ''}${e.nota ? ` · ${e.nota}` : ''}`;
  }
  if (e.tipo === 'stato') {
    return t('evento.stato', { stato: t(`stato.${x.stato}`) })
      + (x.addebitoNoShow != null ? ` · ${t('evento.addebitoInStato', { importo: eur(x.addebitoNoShow) })}` : '')
      + (e.nota ? ` · ${t(`evento.nota.${e.nota}`)}` : '');
  }
  if (e.tipo === 'addebito') return t('evento.addebito', { importo: eur(x.addebitoNoShow) });
  if (e.tipo === 'salute') return t('evento.salute');
  return e.tipo;
}

/**
 * Scheda di una prenotazione: dati, cambi di stato (secondo il ruolo), modifica, salute (solo personale SPA), storico.
 * @param {object} vista come per apriModulo, più dispositivo
 */
export function apriScheda(vista, id) {
  const { ctx } = vista;
  const prenota = puoPrenotare(vista.ruoli);
  const resp = admin(vista.ruoli);
  const op = miaColonna(vista.dispositivo);
  const nomi = vista.nomi();
  let b = vista.dati()?.prenotazioni.find((x) => x.id === id) || null;
  let conferma = null;
  let erroriAzione = [];
  let storico = [];
  const corpo = h('div', { classe: 'scheda', id: 'scheda' });
  const azioni = h('div', { classe: 'azioni-stato', id: 'd-azioni' });
  const log = h('details', { classe: 'storico', id: 'd-storico' });
  const sal = vedeSalute(vista.ruoli) ? boxSalute() : null;
  const { testa, alChiudere } = apriFoglio({ titolo: '', chiudi: t('foglio.chiudi') }, corpo, azioni, sal?.box, log);
  const stop = [];
  alChiudere(() => stop.forEach((f) => f()));

  const datiDelGiorno = () => (b && vista.dati()?.data === b.data ? vista.dati() : null);

  function disegna() {
    if (!b) { sostituisci(corpo, h('p', {}, t('scheda.nonTrovata'))); sostituisci(azioni); return; }
    const tr = TRATTAMENTI[b.tr];
    testa.querySelector('.sopra').textContent = giornoLungo(b.data);
    testa.querySelector('h2').textContent = `${hm(toMin(b.inizio))}–${hm(fine(b))} · ${salaNome(b.sala)} ${b.lettino}`;
    const giorno = datiDelGiorno();
    const probs = giorno && occupa(b) ? problemi(b, giorno, { ignora: [b.id], nomi }) : [];
    const partner = b.coppia && giorno ? giorno.prenotazioni.find((o) => o.coppia === b.coppia && o.id !== b.id) : null;
    const riga = (k, ...valori) => h('div', {}, h('dt', {}, t(k)), h('dd', {}, ...valori));
    sostituisci(corpo,
      h('p', { classe: 'riga-stato' }, h('span', { classe: `chip st-${b.stato}`, id: 'd-stato' }, testoStato(b)),
        b.inSospeso ? h('em', {}, t('agenda.inSospeso')) : null),
      probs.length ? h('ul', { classe: 'msgs' }, probs.map((p) => h('li', { classe: 'e' }, p))) : null,
      h('dl', { classe: 'fatti' },
        riga('scheda.ospite', `${b.camera || t('scheda.senzaCamera')} · ${b.nome}`),
        riga('scheda.trattamento', `${tr?.nome || b.tr} · ${b.durata}'`,
          tr && b.durata !== tr.durata ? ` (${t('scheda.listinoDurata', { durata: tr.durata })})` : ''),
        riga('scheda.operatrice', nomeOperatrice(b.op, nomi)),
        riga('scheda.pagamento', `${t(`tipo.${b.tipo}`)} · ${eur(b.importo)}`, b.motivo ? ` · ${b.motivo}` : '',
          b.listino != null && b.tipo === 'pagamento' && b.importo !== b.listino ? ` (${t('scheda.listinoPrezzo', { prezzo: eur(b.listino) })})` : ''),
        b.est ? riga('scheda.esterno', [b.est.tel, b.est.email].filter(Boolean).join(' · ') || t('scheda.senzaContatti'),
          ` · ${t('scheda.pagaIn', { come: t(`paga.${b.est.paga}`) })}`,
          b.est.caparra ? ` · ${t('scheda.caparra', { importo: eur(b.est.caparra) })}` : '') : null,
        b.motivoSconto ? riga('scheda.sconto', b.motivoSconto) : null,
        b.stato === 'no-show' && pagante(b)
          ? riga('scheda.addebitoNoShow', noShowDaDecidere(b) ? t('scheda.daDecidere') : eur(b.addebitoNoShow || 0)) : null,
        partner ? riga('scheda.coppiaCon', `${partner.nome} · ${salaNome(partner.sala)} ${partner.lettino} · ${nomeOperatrice(partner.op, nomi)}`) : null,
        b.nota ? riga('scheda.nota', b.nota) : null));
    disegnaAzioni();
  }

  function disegnaAzioni() {
    if (!b) return;
    if (conferma) {
      const c = conferma;
      // L'importo scritto resta in «c» anche se la scheda si ridisegna (arriva un aggiornamento dal server).
      const importo = c.campo ? h('input', {
        id: 'd-noshow-importo', inputmode: 'decimal', autocomplete: 'off', maxlength: 8, value: c.campo.valore ?? '',
        oninput: (ev) => { c.campo.valore = ev.target.value; },
      }) : null;
      sostituisci(azioni,
        c.testo ? h('p', {}, c.testo) : null,
        importo ? campo(t('noshow.importo', { max: eur(b.importo) }), importo) : null,
        c.errore ? h('ul', { classe: 'msgs' }, h('li', { classe: 'e', id: 'd-noshow-errore' }, c.errore)) : null,
        h('button', { type: 'button', onclick: () => { conferma = null; disegnaAzioni(); } }, t('scheda.indietro')),
        h('button', { type: 'button', classe: 'primario', id: 'd-conferma', onclick: applica }, c.ok));
      return;
    }
    sostituisci(azioni,
      erroriAzione.length ? h('ul', { classe: 'msgs' }, erroriAzione.map((x) => h('li', { classe: 'e' }, x))) : null,
      azioniStato(b, { prenota, op }).map((s) => h('button', {
        type: 'button', classe: s === 'fatto' ? 'primario' : 'secondario', dati: { azione: s }, onclick: () => chiedi(s),
      }, t(`azione.${s}`))),
      // L'importo del no-show lo scrive (o cambia) solo la responsabile.
      resp && b.stato === 'no-show' && pagante(b) ? h('button', {
        type: 'button', classe: 'secondario', id: 'd-addebito',
        onclick: () => {
          conferma = { testo: '', campo: { valore: noShowDaDecidere(b) ? '' : String(b.addebitoNoShow ?? '') }, decide: true, ok: t('noshow.salva') };
          disegnaAzioni();
        },
      }, noShowDaDecidere(b) ? t('noshow.decidi') : t('noshow.cambia')) : null,
      prenota ? h('button', { type: 'button', classe: 'secondario', id: 'd-modifica', onclick: () => apriModulo(vista, { prenotazione: b }) }, t('scheda.modifica')) : null);
  }

  // Conferma: con il campo dell'importo (no-show deciso dalla responsabile) lo controlla prima di salvare.
  function applica() {
    const c = conferma;
    if (!c.campo) { esegui(c.stato, c.extra); return; }
    const valore = importoNoShow(c.campo.valore, b);
    if (valore === null) { c.errore = t('noshow.errore', { max: eur(b.importo) }); disegnaAzioni(); return; }
    if (!c.decide) { esegui(c.stato, { ...c.extra, addebitoNoShow: valore }); return; }
    conferma = null;
    const r = vista.scritture.decidiNoShow(b.id, valore);
    seguiInvio(r.conferma, t('salvato.addebito'), testiInvio());
    disegnaAzioni();
  }

  function chiedi(s) {
    erroriAzione = [];
    if (s === 'disdetta') {
      // Regola del menù: gratis fino a 24 ore prima; dopo, addebito dell'intero importo.
      const x = esitoDisdetta(b, adessoRoma());
      const testo = !x.tardiva ? t('disdetta.gratuita', { ore: Math.floor(x.ore) })
        : x.pagante ? t('disdetta.tardiva', { importo: eur(b.importo) }) : t('disdetta.tardivaGratis');
      conferma = { testo, stato: 'disdetta', extra: { addebito: x.addebito, nota: x.tardiva ? 'tardiva' : 'gratuita' }, ok: t('disdetta.conferma') };
      disegnaAzioni();
      return;
    }
    if (s === 'no-show') {
      // Chi paga: l'importo da addebitare lo scrive la responsabile (adesso, se è lei a segnare il no-show; sennò dopo).
      conferma = {
        testo: t('noshow.testo') + (pagante(b) && !resp ? ` ${t('noshow.daDecidere')}` : ''),
        stato: 'no-show', extra: {}, ok: t('noshow.conferma'),
        campo: pagante(b) && resp ? { valore: '' } : null,
      };
      disegnaAzioni();
      return;
    }
    if (s === 'confermata' && !occupa(b)) {
      // Riaprire una disdetta o un no-show: il posto deve essere ancora libero.
      const giorno = datiDelGiorno();
      const p = giorno ? problemi({ ...b, stato: 'confermata' }, giorno, { ignora: [b.id], nomi }) : [];
      if (p.length) { erroriAzione = p; disegnaAzioni(); return; }
    }
    esegui(s, s === 'confermata' && b.stato === 'disdetta' ? { addebito: false } : {});
  }

  function esegui(stato, extra) {
    conferma = null;
    const r = vista.scritture.cambiaStato(b.id, stato, extra);
    seguiInvio(r.conferma, t('salvato.stato', { stato: t(`stato.${stato}`) }), testiInvio());
    disegnaAzioni();
  }

  function disegnaStorico() {
    sostituisci(log, h('summary', {}, t('scheda.storico', { n: storico.length })),
      h('ol', {}, storico.map((e) => h('li', {}, h('time', {}, quandoBreve(e.quando)), ' ', testoEvento(e, nomi),
        e.daNome ? h('small', {}, ` · ${e.daNome}`) : null))));
  }

  // Salute nella scheda: la vede e la cambia solo il personale SPA (anche l'operatrice).
  function boxSalute() {
    const c = campiSalute('d-salute');
    let toccata = false;
    let avviata = false;
    const salva = h('button', {
      type: 'button', classe: 'secondario', id: 'd-salva-salute', disabled: true,
      onclick: () => {
        const r = vista.scritture.salvaSalute(b, c.leggi());
        toccata = false;
        seguiInvio(r.conferma, t('salvato.salute'), testiInvio());
      },
    }, t('salute.salva'));
    c.box.append(h('div', { classe: 'azioni' }, salva));
    c.box.addEventListener('input', () => { toccata = true; });
    c.blocca(true);
    return {
      box: c.box,
      avvia() {
        if (avviata || !b) return;
        avviata = true;
        stop.push(ascoltaSalute(ctx, idSalute(b), (x) => {
          if (!toccata) c.metti(x);
          c.blocca(false);
          salva.disabled = false;
        }, (e) => ctx.scatola.registra(e, { dove: 'scheda-salute' })));
      },
    };
  }

  stop.push(ascoltaPrenotazione(ctx, id, (x) => { b = x; disegna(); sal?.avvia(); }, (e) => ctx.scatola.registra(e, { dove: 'scheda' })));
  // Lo storico senza dati di salute; gli eventi «salute» (solo «aggiornata») li vede solo il personale SPA.
  stop.push(ascoltaStorico(ctx, id, (lista) => {
    storico = sal ? lista : lista.filter((e) => e.tipo !== 'salute');
    disegnaStorico();
  }, (e) => ctx.scatola.registra(e, { dove: 'storico' })));
  disegna();
  sal?.avvia();
  disegnaStorico();
}
